import type {
  AlertRuleSpec,
  AlertRuleView,
  AlertTrigger,
  MarketKind,
  SideView,
} from "@screener/protocol";
import { isRecord } from "@/lib/guards";
import { DAEMON_REQUEST_TIMEOUT_MS } from "@/lib/request-timeout";
import type { ApiResult } from "./api-result";

/**
 * Клиент правил алертов §6.5 запросного канала §7.6 для экрана «Алерты»
 * (design.md решение 6): полный разбор `AlertRuleView`, отдельный от
 * терпимого `alerts/alert-rules-api.ts`, который читает тот же
 * `GET /api/alert-rules` только ради `id`/`sound`/`notify` вкладки. Разбор
 * списка — поштучно: негодный элемент отбрасывается с `console.error`,
 * остальные правила списка остаются (design.md решение 6, стиль
 * `alert-rules-api.ts`).
 */

const RULES_BASE = "/api/alert-rules";

/**
 * Полнота проверяется компилятором: `satisfies Record<AlertTrigger, true>`
 * не собирается, если протокол заводит новый вариант триггера, а эта карта
 * его не перечисляет (ревью stage6-alert-web, пункт C.5).
 */
const ALERT_TRIGGERS = {
  confirmed: true,
  approached: true,
  tested: true,
  held: true,
  eaten: true,
  pulled: true,
  moved: true,
  situation_stacked: true,
} satisfies Record<AlertTrigger, true>;

function isAlertTrigger(value: unknown): value is AlertTrigger {
  return typeof value === "string" && Object.hasOwn(ALERT_TRIGGERS, value);
}

const MARKET_KINDS = {
  spot: true,
  perp: true,
} satisfies Record<MarketKind, true>;

function isMarketKind(value: unknown): value is MarketKind {
  return typeof value === "string" && Object.hasOwn(MARKET_KINDS, value);
}

const SIDES = {
  bid: true,
  ask: true,
} satisfies Record<SideView, true>;

function isSideView(value: unknown): value is SideView {
  return typeof value === "string" && Object.hasOwn(SIDES, value);
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

function isAlertRuleSpec(value: unknown): value is AlertRuleSpec {
  return (
    isRecord(value) &&
    isNullableString(value.exchange) &&
    (value.market === null || isMarketKind(value.market)) &&
    isNullableString(value.base) &&
    (value.side === null || isSideView(value.side)) &&
    isNullableString(value.min_usd) &&
    isNullableString(value.max_usd) &&
    typeof value.favorite_only === "boolean" &&
    typeof value.exclude_mm === "boolean" &&
    isAlertTrigger(value.trigger) &&
    typeof value.sound === "boolean" &&
    typeof value.notify === "boolean" &&
    typeof value.telegram === "boolean" &&
    typeof value.log === "boolean"
  );
}

function isAlertRuleView(value: unknown): value is AlertRuleView {
  return (
    isRecord(value) &&
    typeof value.id === "string" &&
    isAlertRuleSpec(value.rule) &&
    typeof value.created_at_ms === "number"
  );
}

/** Разбирает список — негодный элемент отбрасывается поштучно. */
function parseRuleList(body: unknown): AlertRuleView[] | undefined {
  if (!Array.isArray(body)) {
    return undefined;
  }
  const rules: AlertRuleView[] = [];
  for (const item of body) {
    if (isAlertRuleView(item)) {
      rules.push(item);
    } else {
      console.error("правило алерта отброшено: не разобралось как AlertRuleView", item);
    }
  }
  return rules;
}

async function readErrorStatus(response: Response): Promise<ApiResult<never> | undefined> {
  if (response.status === 401) {
    return { status: "unauthorized" };
  }
  if (response.status === 404) {
    return { status: "not_found" };
  }
  if (response.status === 400 || response.status === 422) {
    return { status: "invalid" };
  }
  if (!response.ok) {
    console.error("запрос правил алертов отклонён демоном", response.status);
    return { status: "unavailable" };
  }
  return undefined;
}

export async function listRules(): Promise<ApiResult<AlertRuleView[]>> {
  let response: Response;
  try {
    response = await fetch(RULES_BASE, { signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS) });
  } catch {
    return { status: "unavailable" };
  }
  const error = await readErrorStatus(response);
  if (error) {
    return error;
  }
  try {
    const body: unknown = await response.json();
    const rules = parseRuleList(body);
    if (!rules) {
      return { status: "unavailable" };
    }
    return { status: "ok", value: rules };
  } catch {
    return { status: "unavailable" };
  }
}

async function writeRule(
  url: string,
  method: "POST" | "PUT",
  spec: AlertRuleSpec,
): Promise<ApiResult<AlertRuleView>> {
  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(spec),
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  const error = await readErrorStatus(response);
  if (error) {
    return error;
  }
  try {
    const body: unknown = await response.json();
    if (!isAlertRuleView(body)) {
      return { status: "unavailable" };
    }
    return { status: "ok", value: body };
  } catch {
    return { status: "unavailable" };
  }
}

export async function createRule(spec: AlertRuleSpec): Promise<ApiResult<AlertRuleView>> {
  return writeRule(RULES_BASE, "POST", spec);
}

export async function replaceRule(
  id: string,
  spec: AlertRuleSpec,
): Promise<ApiResult<AlertRuleView>> {
  return writeRule(`${RULES_BASE}/${encodeURIComponent(id)}`, "PUT", spec);
}

export async function removeRule(id: string): Promise<ApiResult<null>> {
  let response: Response;
  try {
    response = await fetch(`${RULES_BASE}/${encodeURIComponent(id)}`, {
      method: "DELETE",
      signal: AbortSignal.timeout(DAEMON_REQUEST_TIMEOUT_MS),
    });
  } catch {
    return { status: "unavailable" };
  }
  if (response.status === 204) {
    return { status: "ok", value: null };
  }
  const error = await readErrorStatus(response);
  if (error) {
    return error;
  }
  console.error("правило алерта не удалено демоном", response.status);
  return { status: "unavailable" };
}
